import { decamelizeKeys } from 'humps';

import {
  clearAuthTokens,
  refreshAccessTokenNow,
  setAuthTokens,
  type RefreshResponse,
} from '../apiClient';
import { authClient } from '../authClient';

import { emitUnauthorized } from './authEvents';

export type LoginResponse = {
  token: string;
  refresh: string;
  expires: string;
};

export type TwoFactorRequiredResponse = {
  twoFactorRequired: true;
  lastFourDigits: string;
};

export type LoginResult =
  | { status: 200; data: LoginResponse }
  | { status: 202; data: TwoFactorRequiredResponse };

/**
 * Apigee login. Mirrors the login app's wiring (POST /v1/auth/login).
 *   200 → {token, refresh, expires}. Tokens are stashed on apiClient so every
 *         subsequent B2B call carries `Authorization: Bearer <token>`, and a
 *         401 will trigger an automatic refresh.
 *   202 → {twoFactorRequired: true, lastFourDigits}. SMS code was sent;
 *         call login(...) again with `twoFactorCode` to complete.
 *
 * Omit `twoFactorCode` on the first call. Pass it on the second call to
 * verify the SMS code.
 */
export async function login(
  email: string,
  password: string,
  twoFactorCode?: string
): Promise<LoginResult> {
  const body = decamelizeKeys({
    email,
    password,
    ...(twoFactorCode ? { twoFactorCode } : {}),
  });
  const response = await authClient.post('/v1/auth/login', body);
  if (response.status === 200) {
    const data = response.data as LoginResponse;
    await setAuthTokens(data);
  }
  return { status: response.status as 200 | 202, data: response.data };
}

/**
 * Redeem a one-time native-WebView handoff code for a Bearer token pair.
 *
 * When native onboarding is served by this app, Sparta (the entry broker at
 * www.vida.com/clients/onboarding) exchanges its Django session for Apigee
 * tokens via POST /v1/auth/session-exchange, which stashes the pair under a
 * short-TTL opaque code and redirects the WebView here with `?handoff=<code>`.
 * This call redeems that code. The code is single-use server-side, so a
 * reused/expired code fails and the caller falls back to unauthenticated
 * entry (account creation / sign-in).
 */
export async function redeemHandoffCode(handoff: string): Promise<boolean> {
  try {
    const response = await authClient.post('/v1/auth/handoff-redeem', { handoff });
    if (response.status !== 200) return false;
    await setAuthTokens(response.data as LoginResponse);
    return true;
  } catch {
    // 404/410 (unknown or already-used code), network failure — all
    // non-fatal: entry degrades to the unauthenticated flow.
    return false;
  }
}

/**
 * Explicit refresh using the stored refresh token. Most callers don't need
 * this — apiClient's response interceptor auto-refreshes on 401 and replays
 * the original request. Use this for proactive refreshes (e.g. on app
 * resume) where you'd rather not wait for a 401 round-trip.
 *
 * Throws if no refresh token is stored or the refresh call itself fails.
 */
export async function refreshToken(): Promise<RefreshResponse> {
  const result = await refreshAccessTokenNow();
  if (!result) throw new Error('Refresh failed — no valid refresh token');
  return result;
}

/** Clears both tokens. Use on sign-out / app reset. */
export async function clearAccessToken(): Promise<void> {
  await clearAuthTokens();
  emitUnauthorized();
}
